export function addTextInHTML(html, texto) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const paragraphs = doc.querySelector('p');

  if (paragraphs) {
    paragraphs.innerHTML = `${texto} ${paragraphs.innerHTML}`;
  }

  return doc.body.innerHTML;
}

/**
 * Limpia el texto de etiquetas HTML y entidades HTML
 * @param {string} html - Texto con HTML a limpiar
 * @returns {string} Texto limpio
 */
export function cleanHTML(html) {
  if (!html) return '';

  // Primero convertimos el HTML a texto plano
  const doc = new DOMParser().parseFromString(html, 'text/html');
  let text = doc.body.textContent || '';

  // Limpiamos espacios extras
  text = text.trim().replace(/\s+/g, ' ');

  return text;
}

// Etiquetas que puede producir el WYSIWYG del CMS. Es un listado cerrado a propósito: `cleanHTML`
// parsea la cadena entera como HTML, así que un "<" literal de un título ("x<y", "2 < 3") se
// tomaría por el inicio de una etiqueta y se perdería cuanto va detrás. Reconociendo solo
// estas etiquetas, cualquier otro "<" se escapa antes de parsear y sobrevive como texto.
// Límite conocido: una etiqueta fuera del listado (un tag exótico o un custom element) se
// mostraría literal en vez de eliminarse.
const KNOWN_TAGS = new Set([
  'a',
  'b',
  'blockquote',
  'br',
  'code',
  'div',
  'em',
  'figcaption',
  'figure',
  'font',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'i',
  'img',
  'li',
  'mark',
  'ol',
  'p',
  'pre',
  's',
  'section',
  'small',
  'span',
  'strong',
  'sub',
  'sup',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'u',
  'ul',
]);

// Etiquetas que separan visualmente el texto: `textContent` concatena sin separador, así que un
// título con salto de línea o con dos bloques llegaría con las palabras pegadas ("L1<br>L2").
const BLOCK_TAGS = new Set([
  'blockquote',
  'br',
  'div',
  'figcaption',
  'figure',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'hr',
  'li',
  'ol',
  'p',
  'pre',
  'section',
  'table',
  'tbody',
  'td',
  'th',
  'thead',
  'tr',
  'ul',
]);

// Solo la forma de una etiqueta: el nombre se coteja contra KNOWN_TAGS al recorrer.
const HTML_TAG = /<\/?([a-z][a-z0-9]*)\b[^<>]*>/gi;

function normalizeMarkup(value) {
  let result = '';
  let cursor = 0;
  let tag;

  HTML_TAG.lastIndex = 0;
  while ((tag = HTML_TAG.exec(value)) !== null) {
    // Un token con forma de etiqueta pero nombre desconocido se trata como texto: su '<' se
    // escapa igual que cualquier otro suelto (el token no puede contener otro '<', así que
    // escaparlo aquí es idéntico a no haberlo reconocido).
    const name = tag[1].toLowerCase();
    const keepAsTag = KNOWN_TAGS.has(name);
    const token = keepAsTag ? tag[0] : tag[0].replaceAll('<', '&lt;');
    const separator = keepAsTag && BLOCK_TAGS.has(name) ? ' ' : '';
    result += value.slice(cursor, tag.index).replaceAll('<', '&lt;') + token + separator;
    cursor = tag.index + tag[0].length;
  }

  return result + value.slice(cursor).replaceAll('<', '&lt;');
}

/**
 * Texto plano de un valor editado con WYSIWYG ("<p>Unidad 1</p>"), para pintarlo como texto o
 * usarlo en nombres accesibles y breadcrumbs. Endurece `cleanHTML` en los dos puntos donde este
 * pierde información: los "<" literales y los bloques sin separador.
 * @param {string} value - Valor con posible markup
 * @returns {string} Texto plano
 */
export function htmlToPlainText(value) {
  if (typeof value !== 'string' || !value) return '';

  return cleanHTML(normalizeMarkup(value));
}

/**
 * Convierte una cadena CSS con variables (formato :root) en un objeto JavaScript
 * Las claves del objeto resultante están en camelCase y se eliminan los prefijos '--'
 * Los valores de URL se limpian eliminando 'url(' y ')'
 *
 * @param {string} cssString - String CSS con variables en formato :root {...}
 * @returns {Object} Objeto con las variables CSS como propiedades
 *
 * @example
 * // Input: ":root{--color-primary:#ff0000;--bg-image:url('img.jpg');}"
 * // Output: {colorPrimary: "#ff0000", bgImage: "img.jpg"}
 */

export function parseCSSVarsToObject(cssString) {
  // Remove :root {} and split by semicolon
  const cleanString = cssString?.replace(/:root\s*{/, '')?.replace(/}$/, '');
  const varsArray = cleanString?.split(';').filter(Boolean);

  return (
    varsArray?.reduce((acc, variable) => {
      // Find the position of the first colon
      const colonIndex = variable?.indexOf(':');
      if (colonIndex === -1) return acc;

      // Split into key and value using the first colon only
      const key = variable?.substring(0, colonIndex)?.trim();
      const value = variable?.substring(colonIndex + 1)?.trim();

      if (key && value) {
        // Remove '--' from key and convert to camelCase
        const propertyName = key?.replace('--', '').replace(/-([a-z])/g, (g) => g[1].toUpperCase());

        // Validate URL values
        if (value.startsWith('url(')) {
          const urlValue = value.replace(/^url\(['"]?/, '').replace(/['"]?\)$/, '');
          try {
            new URL(urlValue);
            acc[propertyName] = urlValue;
          } catch {
            acc[propertyName] = undefined;
          }
        } else {
          acc[propertyName] = value;
        }
      }
      return acc;
    }, {}) || {}
  );
}
